/**
 * @file DigestEditor Component
 * @description Admin interface for generating and viewing daily digest reports.
 * Provides a "Generate Digest" button, displays the executive summary,
 * activity counts, and digest history.
 */

import {
  useDigestHistory,
  useLatestDigest,
  type DigestHistoryItem,
} from '@medsuite/api-client';
import { useState, useEffect } from 'react';

import { DigestViewerModal } from '../../../components/DigestViewerModal';
import { useDigestNotification } from '../../../providers/DigestNotificationProvider';

/**
 * Format a date string for display
 */
function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Format a date string as short date
 */
function formatShortDate(dateStr: string): string {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

/**
 * Format time only
 */
function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Format short datetime (month day hour:minute)
 */
function formatShortDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Calculate time ago from a date string
 */
function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }
  if (diffHours > 0) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }
  if (diffMin > 0) {
    return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  }
  return 'just now';
}

interface DigestEditorProps {
  onDataLoaded?: (count: number) => void;
}

/**
 * Format activity count for display
 */
function formatCount(label: string, count: number): string {
  return `${count} ${label}${count === 1 ? '' : 's'}`;
}

export default function DigestEditor({ onDataLoaded }: DigestEditorProps) {
  const [selectedDigestId, setSelectedDigestId] = useState<number | null>(null);
  const { startGeneration, isGenerating } = useDigestNotification();

  const { data: history = [], isLoading: historyLoading } = useDigestHistory(10, {
    refetchOnWindowFocus: false,
  });

  const { data: latestDigest, isLoading: latestLoading } = useLatestDigest({
    refetchOnWindowFocus: false,
  });

  // Report count to parent (must be in useEffect to avoid setState during render)
  const displayCount = history.length;
  useEffect(() => {
    onDataLoaded?.(displayCount);
  }, [onDataLoaded, displayCount]);

  const handleGenerate = () => {
    startGeneration();
  };

  const isLoading = historyLoading || latestLoading;

  return (
    <div className="space-y-6">
      {/* Generate Button Section */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Daily Digest Report
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Generate an AI-powered summary of investment activity
          </p>
        </div>
        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating...
            </>
          ) : (
            'Generate Digest'
          )}
        </button>
      </div>

      {/* Last Generated Info */}
      {latestDigest && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <svg
              className="h-5 w-5 text-gray-400 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Last digest generated {timeAgo(latestDigest.generated_at)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                By {latestDigest.generated_by} on {formatDateTime(latestDigest.generated_at)}
              </p>
              {latestDigest.counts && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formatCount('decision', latestDigest.counts.decisions)},{' '}
                  {formatCount('status change', latestDigest.counts.status_changes)},{' '}
                  {formatCount('new investment', latestDigest.counts.new_investments)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Digest History */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Digest History
        </h4>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <svg
              className="animate-spin h-6 w-6 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <svg
              className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="mt-2 text-sm">No digests generated yet</p>
            <p className="text-xs">Click "Generate Digest" to create your first report</p>
          </div>
        ) : (
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100">
                    Generated
                  </th>
                  <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900 dark:text-gray-100">
                    Period
                  </th>
                  <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900 dark:text-gray-100">
                    By
                  </th>
                  <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900 dark:text-gray-100">
                    Activity
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                {history.map((item: DigestHistoryItem) => (
                  <tr
                    key={item.id}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => setSelectedDigestId(item.id)}
                  >
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 dark:text-gray-100">
                      <div>{formatShortDate(item.generated_at)}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatTime(item.generated_at)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                      <div>{formatShortDateTime(item.period.start)}</div>
                      <div className="text-xs">to {formatShortDateTime(item.period.end)}</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {item.generated_by}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {item.counts ? (
                        <div className="flex flex-wrap gap-1">
                          {item.counts.decisions > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                              {item.counts.decisions} decisions
                            </span>
                          )}
                          {item.counts.status_changes > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                              {item.counts.status_changes} status
                            </span>
                          )}
                          {item.counts.at_risk_items > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                              {item.counts.at_risk_items} at-risk
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Digest Viewer Modal for history row click */}
      <DigestViewerModal
        isOpen={selectedDigestId != null}
        onClose={() => setSelectedDigestId(null)}
        digestId={selectedDigestId}
      />
    </div>
  );
}
